import { useEffect, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { pondsApi } from '@/features/ponds/api/pondsApi';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { getSavedPondId, savePondId, clearPondId } from '@/shared/lib/pondIdStorage';
import type { PondResponse } from '@/shared/types/api';

/**
 * Tab configuration for feature pages
 */
export interface FeatureTab {
  /** Unique tab identifier (e.g., 'records', 'trends', 'alerts') */
  id: string;
  /** Path segment for the tab (empty string for base route, '/trends' for trends tab) */
  path: string;
}

/**
 * Options for useFeaturePageState hook
 */
export interface UseFeaturePageStateOptions {
  /** Farm ID from URL params */
  farmId: string | undefined;
  /** Feature name used in URL paths (e.g., 'water-quality', 'fish-health') */
  feature: 'water-quality' | 'fish-health' | 'maintenance' | 'feed';
  /** Tab configurations with id and path */
  tabs: FeatureTab[];
  /** Currently active tab id */
  currentTab: string;
}

/**
 * Return type for useFeaturePageState hook
 */
export interface UseFeaturePageStateReturn {
  /** Currently selected pond ID from URL */
  selectedPondId: string | null;
  /** Set the selected pond ID (updates URL and localStorage) */
  setSelectedPondId: (pondId: string | null) => void;
  /** Navigate to a different tab while preserving pondId */
  navigateToTab: (tabId: string) => void;
  /** List of ponds for the farm */
  ponds: PondResponse[];
  /** Whether ponds are currently loading */
  isPondsLoading: boolean;
}

/**
 * Shared hook for managing pond selection and tab navigation state
 * across Water Quality, Fish Health, Maintenance, and Feed features.
 *
 * Features:
 * - URL-first state management (pondId in query params)
 * - Auto-restore from localStorage when navigating without pondId
 * - Tab navigation with path-based routing (/feature/trends)
 * - Preserves pondId when switching tabs
 *
 * @example
 * ```tsx
 * const { selectedPondId, setSelectedPondId, navigateToTab, ponds, isPondsLoading } = useFeaturePageState({
 *   farmId,
 *   feature: 'fish-health',
 *   tabs: [
 *     { id: 'records', path: '' },
 *     { id: 'trends', path: '/trends' }
 *   ],
 *   currentTab: 'records'
 * });
 * ```
 */
export function useFeaturePageState(options: UseFeaturePageStateOptions): UseFeaturePageStateReturn {
  const { farmId, feature, tabs } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const previousFarmIdRef = useRef<string | undefined>(farmId);

  // Fetch ponds for the farm
  const { data: ponds = [], isLoading: isPondsLoading } = useQuery({
    queryKey: ['ponds', farmId],
    queryFn: () => pondsApi.list(farmId!),
    enabled: !!farmId,
  });

  // Get pond IDs from different sources
  const urlPondId = searchParams.get('pondId');
  const cachedPondId = user?.id ? getSavedPondId(user.id) : null;

  // Compute the effective selected pond ID synchronously (no jolt!)
  // Priority: URL > localStorage (if valid)
  const selectedPondId = useMemo(() => {
    // If URL has pondId, use it (will be validated below)
    if (urlPondId) {
      // Validate it exists in ponds list if ponds are loaded
      if (ponds.length > 0) {
        const pondExists = ponds.some((p) => p.id === urlPondId);
        return pondExists ? urlPondId : null;
      }
      // Ponds not loaded yet, trust URL for now
      return urlPondId;
    }

    // No URL pondId - try localStorage if ponds are loaded
    if (!isPondsLoading && ponds.length > 0 && cachedPondId) {
      const pondExists = ponds.some((p) => p.id === cachedPondId);
      if (pondExists) return cachedPondId;
    }

    return null;
  }, [urlPondId, cachedPondId, ponds, isPondsLoading]);

  // Sync URL with effective selectedPondId (using layoutEffect for faster visual update)
  // This runs before paint, minimizing visual jolt
  useLayoutEffect(() => {
    // If we have a valid cached pondId but URL doesn't have it, add to URL
    if (!urlPondId && selectedPondId && farmId && ponds.length > 0) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('pondId', selectedPondId);
      setSearchParams(newParams, { replace: true });
    }
    // If URL has invalid pondId, remove it
    else if (urlPondId && ponds.length > 0 && !selectedPondId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('pondId');
      setSearchParams(newParams, { replace: true });
    }
  }, [urlPondId, selectedPondId, farmId, ponds.length, searchParams, setSearchParams]);

  // Clear pond selection when farm changes
  useEffect(() => {
    if (previousFarmIdRef.current && farmId && previousFarmIdRef.current !== farmId) {
      if (user?.id) {
        clearPondId(user.id);
      }
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('pondId');
      setSearchParams(newParams, { replace: true });
    }
    previousFarmIdRef.current = farmId;
  }, [farmId, searchParams, setSearchParams, user?.id]);

  // Set pond ID - updates URL and saves to localStorage
  const userId = user?.id;
  const setSelectedPondId = useCallback(
    (pondId: string | null) => {
      const newParams = new URLSearchParams(searchParams);
      if (pondId) {
        newParams.set('pondId', pondId);
        if (userId) {
          savePondId(userId, pondId);
        }
      } else {
        newParams.delete('pondId');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams, userId]
  );

  // Navigate to a different tab while preserving pondId
  const navigateToTab = useCallback(
    (tabId: string) => {
      if (!farmId) return;

      const tab = tabs.find((t) => t.id === tabId);
      if (!tab) return;

      // Use effective selectedPondId (already validated)
      const currentPondId = selectedPondId;

      // Build the new URL with path-based tab routing
      const basePath = `/farms/${farmId}/${feature}${tab.path}`;

      // Preserve pondId in query params
      const params = new URLSearchParams();
      if (currentPondId) {
        params.set('pondId', currentPondId);
      }

      // Preserve any other existing query params (like metrics for trends)
      const currentParams = new URLSearchParams(location.search);
      currentParams.forEach((value, key) => {
        if (key !== 'pondId' && key !== 'tab') {
          params.set(key, value);
        }
      });

      const queryString = params.toString();
      const url = basePath + (queryString ? `?${queryString}` : '');
      navigate(url);
    },
    [farmId, feature, tabs, selectedPondId, location.search, navigate]
  );

  return {
    selectedPondId,
    setSelectedPondId,
    navigateToTab,
    ponds,
    isPondsLoading,
  };
}
